export interface InventoryRegistrationPlan {
  groupName: string;
  revision: number;
  operationId: string;
  transferId: string;
  transferName: string;
  sourceName: string;
  destinationName: string;
  items: Array<{ id: string; sku: string; productTitle: string; quantity: number }>;
}

export interface InventoryRegistrationSyncResult { groupName: string; ok: boolean; message: string }

export interface InventoryRegistrationResult { groupName: string; ok: boolean; added?: number; transferName?: string; message?: string }

export function inventoryRegistrationAllSucceeded(results: readonly InventoryRegistrationResult[], groups: readonly string[]): boolean {
  return groups.length > 0 && results.length === groups.length
    && groups.every((groupName) => results.filter((result) => result.groupName === groupName && result.ok === true).length === 1);
}

export function inventoryRegistrationResultMessage(result: InventoryRegistrationResult): string {
  return result.ok
    ? `${result.groupName}：${result.added ?? 0}件を登録しました（Shopify ${result.transferName ?? ''}）。`
    : `${result.groupName}：${result.message ?? '登録結果を確認できませんでした。'}`;
}

export function inventoryRegistrationSuccessDialogHtml(results: readonly InventoryRegistrationResult[]): string {
  return `<section class="inventory-shopify-detail inventory-shopify-message"><h2 id="inventory-registration-success-title">正常移管登録できました。</h2>
    <div role="status">${results.map((result) => `<p>${escapeHtml(inventoryRegistrationResultMessage(result))}</p>`).join('')}</div>
    <footer class="inventory-shopify-actions"><button type="button" class="button secondary" data-registration-success-close>閉じる</button></footer></section>`;
}

export function inventoryRegistrationSyncSummary(results: readonly InventoryRegistrationSyncResult[] = []): { message: string; failed: boolean } {
  return { message: results.map((result) => `${result.groupName}：${result.message}`).join(' '), failed: results.some((result) => !result.ok) };
}

function escapeHtml(value: unknown): string {
  return String(value).replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#039;');
}

function inventoryRegistrationProgressHtml(message: string, preparing = false): string {
  return `<div class="inventory-registration-progress${preparing ? ' is-preparing' : ''}" data-registration-progress role="status" aria-live="polite"><span class="inventory-registration-spinner" aria-hidden="true"></span><div><strong>${escapeHtml(message)}</strong><small>そのままお待ちください。</small></div></div>`;
}

export function inventoryRegistrationLoadingDialogHtml(): string {
  return `<article class="inventory-transfer-request-form"><header class="inventory-transfer-request-head"><div><p>SHOPIFY INVENTORY TRANSFER</p><h2 id="inventory-registration-title">移管登録</h2></div></header><div class="inventory-transfer-request-body">${inventoryRegistrationProgressHtml('登録内容を確認中…', true)}</div></article>`;
}

export function inventoryRegistrationDialogHtml(plans: readonly InventoryRegistrationPlan[]): string {
  const count = plans.reduce((sum, plan) => sum + plan.items.length, 0);
  return `<form class="inventory-transfer-request-form" data-registration-form>
    <header class="inventory-transfer-request-head"><div><p>SHOPIFY INVENTORY TRANSFER</p><h2 id="inventory-registration-title">移管登録</h2></div><button class="dialog-close" type="button" data-registration-close aria-label="閉じる">×</button></header>
    <div class="inventory-transfer-request-body"><p class="inventory-transfer-request-guide">PSTグループごとにShopifyの在庫移管を下書き登録します。登録済みグループには新しい商品を追加せず、別のPSTグループに振り分けます。発送・入荷確定は行いません。登録済み商品の在庫元・補充元変更はロックされ、「元に戻す」には管理者確認が必要です。</p>
    ${inventoryRegistrationProgressHtml('Shopifyに移管登録中…')}
    ${plans.map((plan) => `<section class="inventory-relocation-confirm-section"><h3>${escapeHtml(plan.groupName)} · ${plan.operationId ? '前回の登録を再確認' : plan.transferId ? '既存の移管に追加' : '新規作成'}</h3>
      <p>${escapeHtml(plan.sourceName || '未選択')} → ${escapeHtml(plan.destinationName || '未選択')}</p>
      ${!plan.transferId ? `<p class="inventory-registration-note">メモ：グループ名：${escapeHtml(plan.groupName)}</p>` : ''}
      ${plan.transferId ? `<p>Shopify移管：${escapeHtml(plan.transferName || plan.transferId)}</p>` : ''}
      <table><thead><tr><th>SKU</th><th>商品名</th><th>追加数量</th></tr></thead><tbody>${plan.items.map((item) => `<tr><td>${escapeHtml(item.sku || '—')}</td><td>${escapeHtml(item.productTitle)}</td><td>${item.quantity}</td></tr>`).join('') || '<tr><td colspan="3">追加する商品はありません（登録済み）。</td></tr>'}</tbody></table></section>`).join('')}
    <p data-registration-result role="status"></p></div>
    <footer class="inventory-transfer-request-actions"><span>${count}件の商品</span><div class="inventory-relocation-confirm-actions"><button class="button secondary" type="button" data-registration-close>取消</button><button class="button inventory-relocation-register" type="submit" ${count === 0 ? 'disabled' : ''}>Shopifyに移管登録する</button></div></footer></form>`;
}
